// service that handle sorting user preferences 

import { Injectable } from '@angular/core';

// localStorage key for storing sorting presets
const STORAGE_KEY = 'sortingDataCollection';

export type TSortingData = Map<string, { order: number; type: 'asc' | 'desc' }>;

export interface TPageSortingData
{
	pageId: string;          // page this preset belongs to
	sortData: TSortingData;  // sorting preset for this page
}

// storage type
interface PersistedPreset    /**< one preset in storage          */
{
	pageId: string;
	// Map is flattened to an array of [key,value] tuples
	sortArr: [string, { order: number; type: 'asc' | 'desc' }][];
}

type LPersistedState = PersistedPreset[];   // many pages, many presets


@Injectable({
	providedIn: 'root' // service (singleton) visibility at 'root', or other scope
})
export class SortingUserPrefService
{
	constructor()
	{
	}

	// store the updated preset for a given page
	storePageSortingPreset(pageId: string, map: TSortingData): void
	{
		const list: LPersistedState = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');

		// Drop every record whose pageId matches the incoming one
		for (let i = list.length - 1; i >= 0; i--) 
		{
			if (list[i].pageId === pageId)
			{
				list.splice(i, 1);
			}
		}

		// Push the fresh preset

		// list.push({ pageId, sortArr: [...map.entries()] });  // this is the same as below, but more a mess :-( 
		list.push({
			pageId,
			sortArr: Array.from(map.entries())
		});

		localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
	}

	// load the sorting preset for a given page
	//
	// currentPreset --> contains : or no data, or default values retrieved from db
	loadPageSortingPresets(pageId: string, currentPreset: TSortingData): TSortingData
	{
		const raw = localStorage.getItem(STORAGE_KEY);

		if (raw)
		{
			const list: LPersistedState = JSON.parse(raw);

			// Grab every preset for that page
			const samePage = list.filter(p => p.pageId === pageId);

			// Merge them into one Map  (later presets overwrite earlier ones)
			const merged = new Map<string, { order: number; type: 'asc' | 'desc' }>();
			samePage.forEach(p =>
			{
				p.sortArr.forEach(([col, cfg]) => merged.set(col, cfg));
			});

			if ( merged.size < 1 )
				// no data from local storage, then consider currentPreset that can contain values from DB
				return currentPreset;
			else 
				return merged;
		}
		else 
		{
			// nothing stored at all
			return new Map<string, { order: number; type: 'asc' | 'desc' }>();
		}
	}

	// delete every preset that matches the pageId 
	deletePageSortingPresets(pageId: string): void
	{
		const raw = localStorage.getItem(STORAGE_KEY);

		if (raw) 
		{
			const list: LPersistedState = JSON.parse(raw);
			const list_to_keep = list.filter(p => p.pageId !== pageId);   // keep others

			if (list_to_keep.length === 0)
			{
				localStorage.removeItem(STORAGE_KEY);       // remove all
			}
			else
			{
				localStorage.setItem(STORAGE_KEY, JSON.stringify(list_to_keep));
			}
		}
	}

	// load ALL presets for a page 
	loadAllPagesPresets(pageId: string): TPageSortingData[]
	{
		const raw = localStorage.getItem(STORAGE_KEY);

		if (raw)
		{
			// data are present in localStorage

			// Parse the raw JSON string into an array of PersistedPreset
			const list: LPersistedState = JSON.parse(raw);

			// return only those presets that match the given pageId
			return list
				.filter(p => p.pageId === pageId)
				.map(p => ({
					pageId,
					sortData: new Map<string, { order: number; type: 'asc' | 'desc' }>(p.sortArr)
				}));
		}
		else
			return [];
	}	
}

